// components/DesktopViolationWarning.jsx
// Displays real-time violation warnings from the desktop proctoring app

import React, { useEffect, useState } from 'react';
import useDesktopViolations from '../hooks/useDesktopViolations';
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

/**
 * Get human-readable message for a violation type
 */
function getViolationMessage(violation) {
  if (!violation) return '';

  switch (violation.type) {
    case 'blocked_process':
      return `Unauthorized application detected: ${violation.process}`;
    case 'ai_service_access':
      return `AI service access detected: ${violation.domain}`;
    case 'ai_app_network':
      return `AI application network activity: ${violation.process}`;
    case 'clipboard_paste':
      return 'Clipboard paste detected';
    case 'unusual_upload':
      return `Unusual network upload detected (${violation.mb_sent?.toFixed(1) || '?'} MB)`;
    default:
      return `Proctoring violation: ${violation.type}`;
  }
}

/**
 * Get icon for violation severity
 */
function getSeverityIcon(severity) {
  switch (severity) {
    case 'critical':
      return '\u26D4'; // no entry
    case 'high':
      return '\u26A0\uFE0F'; // warning
    case 'medium':
      return '\u2139\uFE0F'; // info
    default:
      return '\u26A0\uFE0F';
  }
}

/**
 * Desktop Violation Warning Component
 *
 * Displays a warning banner when the desktop app detects cheating violations.
 * Uses the useDesktopViolations hook for polling.
 *
 * @param {Object} props
 * @param {boolean} props.enabled - Whether to enable violation polling (default: true)
 * @param {number} props.autoDismissMs - Auto-dismiss after ms, 0 to disable (default: 5000)
 * @param {Function} props.onViolation - Callback when violation detected
 */
export function DesktopViolationWarning({
  enabled = true,
  autoDismissMs = 5000,
  onViolation = null,
  debugViolation = null
}) {
  const { latestViolation, clearLatest, isDesktop } = useDesktopViolations({
    enabled,
    onNewViolation: onViolation
  });

  const isDev = process.env.NODE_ENV === 'development';
  const injectedViolation = isDev ? debugViolation : null;
  const effectiveViolation = injectedViolation || latestViolation;
  const effectiveIsDesktop = isDesktop || !!injectedViolation;
  const [lastShownKey, setLastShownKey] = useState(null);

  // Show warning when new violation arrives (toast UI)
  useEffect(() => {
    if (!effectiveIsDesktop || !enabled || !effectiveViolation) return;

    // Deduplicate: use timestamp if available, else a stable-ish JSON key
    const key =
      effectiveViolation.timestamp ??
      `${effectiveViolation.type ?? "unknown"}|${effectiveViolation.severity ?? "medium"}|${effectiveViolation.process ?? ""}|${effectiveViolation.domain ?? ""}|${effectiveViolation.mb_sent ?? ""}`;

    if (key === lastShownKey) return;
    setLastShownKey(key);

    const severity = effectiveViolation.severity || "medium";
    const message = getViolationMessage(effectiveViolation);
    const description = `Severity: ${severity} | This activity has been logged`;

    if (severity === "critical" || severity === "high") {
      toast.error(message, {
        description,
        duration: autoDismissMs,
        icon: <AlertTriangle className="h-5 w-5" />,
      });
    } else if (severity === "medium") {
      toast.warning(message, {
        description,
        duration: autoDismissMs,
        icon: <AlertTriangle className="h-5 w-5" />,
      });
    } else {
      toast(message, {
        description,
        duration: autoDismissMs,
        icon: <AlertTriangle className="h-5 w-5" />,
      });
    }

    // Only clear polled violations; injected debug violations are owned by parent state
    if (!injectedViolation) {
      clearLatest();
    }
  }, [effectiveViolation, effectiveIsDesktop, enabled, autoDismissMs, injectedViolation, clearLatest, lastShownKey]);

  // This component now only *triggers* Sonner toasts.
  return null;
}

export default DesktopViolationWarning;